import { useTranslation } from 'react-i18next';
import { Phone, MessageCircle } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import type { Service } from '@/types/service';
import config from '@/config';

interface ServiceCardProps {
  service: Service;
  compact?: boolean;
}

export default function ServiceCard({ service, compact = false }: ServiceCardProps) {
  const { t, i18n } = useTranslation();
  const isHindi = i18n.language === 'hi';

  const name = isHindi ? service.nameHi : service.name;
  const description = isHindi ? service.descriptionHi : service.description;
  const price = isHindi ? service.priceHi : service.price;

  // Dynamic icon lookup
  const IconComponent = (LucideIcons as unknown as Record<string, React.ComponentType<{ size?: number; className?: string }>>)[service.icon] || LucideIcons.HelpCircle;

  const whatsappMessage = `Hi! I'm interested in your service: ${service.name}. Please share details.`;
  const whatsappUrl = `https://wa.me/${config.contact.whatsapp}?text=${encodeURIComponent(whatsappMessage)}`;

  if (compact) {
    return (
      <div className="flex items-start gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <IconComponent size={24} />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">{name}</h3>
          <p className="mt-1 text-sm text-gray-500 line-clamp-2">{description}</p>
          <p className="mt-2 text-sm font-semibold text-secondary">{price}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md">
      {/* Icon Header */}
      <div className="flex items-center gap-3 border-b border-gray-100 bg-gray-50 p-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <IconComponent size={22} />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">{name}</h3>
          {service.popular && (
            <span className="text-xs font-medium text-accent">{t('services.popular')}</span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col p-4">
        <p className="text-sm text-gray-600">{description}</p>
        <p className="mt-3 text-lg font-bold text-secondary">{price}</p>

        <div className="mt-4 flex gap-2">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-green-500 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-green-600"
          >
            <MessageCircle size={14} />
            {t('services.enquireNow')}
          </a>
          <a
            href={`tel:${config.contact.phone}`}
            className="flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
          >
            <Phone size={14} />
            {t('services.callNow')}
          </a>
        </div>
      </div>
    </div>
  );
}
